import { Link, Outlet } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { brand } from "@/design-system/tokens";
import { TSGoLogo } from "@/components/brand/TSGoLogo";
import { Clock3, ShieldCheck, Tag } from "lucide-react";

/**
 * Layout for authentication screens (Login, Signup, Admin Login).
 *
 * Two-column on desktop:
 *   • Left: brand panel (dominant primary color) with tagline + micro trust cues.
 *   • Right: form area (centered, mobile-first).
 *
 * On mobile, only the form column is shown with a compact brand header.
 */
export function AuthLayout() {
    return (
        <div className="min-h-screen bg-background">
            <div className="grid min-h-screen md:grid-cols-2">
                {/* Brand panel (desktop only) */}
                <aside
                    aria-hidden
                    className="relative hidden overflow-hidden bg-brand text-white md:flex md:flex-col md:justify-between md:p-12"
                >
                    <div className="absolute inset-0 opacity-30">
                        <div className="absolute -left-16 top-10 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
                        <div className="absolute bottom-10 right-0 h-96 w-96 rounded-full bg-accentbrand/40 blur-3xl" />
                    </div>

                    <Link
                        to={ROUTES.splash}
                        className="relative z-10"
                        data-testid="auth-layout-brand"
                    >
                        <TSGoLogo tone="dark" size="lg" />
                    </Link>

                    <div className="relative z-10 max-w-md space-y-6">
                        <h2 className="text-4xl font-extrabold leading-tight lg:text-5xl">
                            {brand.tagline}
                        </h2>
                        <p className="text-white/85">
                            Sign in to unlock instant delivery, order tracking,
                            and personalised savings.
                        </p>

                        <ul className="mt-6 space-y-3 text-sm">
                            <li className="flex items-center gap-3">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/15">
                                    <Clock3 className="h-4 w-4" />
                                </span>
                                Delivered in 10–20 minutes
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/15">
                                    <Tag className="h-4 w-4" />
                                </span>
                                Everyday low prices
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/15">
                                    <ShieldCheck className="h-4 w-4" />
                                </span>
                                Encrypted, secure checkout
                            </li>
                        </ul>
                    </div>

                    <p className="relative z-10 text-xs text-white/70">
                        © {new Date().getFullYear()} TSGo
                    </p>
                </aside>

                {/* Form column */}
                <section className="flex min-h-screen flex-col">
                    {/* Mobile brand strip */}
                    <div className="flex items-center justify-between px-5 py-5 md:hidden">
                        <Link to={ROUTES.splash}>
                            <TSGoLogo size="md" />
                        </Link>
                    </div>

                    <div className="flex flex-1 items-center justify-center px-5 pb-10 sm:px-8 md:pt-10">
                        <div className="w-full max-w-md animate-fade-in">
                            <Outlet />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default AuthLayout;
